/**
 * RivalryProLogin — iter87.78.iso · dedicated login shell for /rivalry-pro
 *
 * Fully isolated surface — NO links or navigation back to /admin (Site
 * Admin) or /platform (Platform Admin). iter87.78.e: back-link removed
 * per operator directive; Pro+ users have zero reachability to operator
 * surfaces from their own console.
 */
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Crown, Loader2, LogIn, Sparkles } from "lucide-react";
import { proApi } from "./api";
import { api } from "@/lib/api";
import { saveSession } from "./session";

const CLERK_ENABLED = !!process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

export default function RivalryProLogin() {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [showResetForm, setShowResetForm] = useState(false);
    // iter87.199.86 — Legacy link visibility is now gated by a Site Admin
    // toggle (`pro_plus_legacy_signin_enabled`) fetched on mount. Default
    // hidden when Clerk is live; auto-shown as a defensive fallback if
    // Clerk isn't configured so users are never fully locked out.
    const [legacyLinkAllowed, setLegacyLinkAllowed] = useState(!CLERK_ENABLED);
    const [showLegacy, setShowLegacy] = useState(!CLERK_ENABLED);
    const [resetEmail, setResetEmail] = useState("");

    useEffect(() => {
        if (!CLERK_ENABLED) return; // legacy already showing as fallback
        let cancelled = false;
        api.getSiteSettings()
            .then((s) => {
                if (cancelled) return;
                const on = Boolean(s?.pro_plus_legacy_signin_enabled);
                setLegacyLinkAllowed(on);
                // Never auto-expand the form; user still has to click.
            })
            .catch(() => { /* silent — keep default hidden */ });
        return () => { cancelled = true; };
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            toast.error("Email and password required");
            return;
        }
        setBusy(true);
        try {
            const r = await proApi.login(email, password);
            saveSession({ token: r.token, user: r.user });
            toast.success("Welcome back");
            navigate("/rivalry-pro");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Invalid email or password");
        }
        setBusy(false);
    };

    const submitReset = async (e) => {
        e.preventDefault();
        if (!resetEmail) return;
        setBusy(true);
        try {
            await proApi.requestPasswordReset(resetEmail);
            toast.success("If that email matches a Rivalry Pro+ account, a reset link is on its way.");
            setShowResetForm(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Unable to send reset link");
        }
        setBusy(false);
    };

    return (
        <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center p-4" data-testid="rivalry-pro-login-shell">
            <div className="brutal-card max-w-md w-full p-6 space-y-4">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-volt border-2 border-black shadow-brutal flex items-center justify-center shrink-0">
                        <Crown className="w-4 h-4 text-black" strokeWidth={2.5} />
                    </div>
                    <h1 className="font-display font-black text-xl tracking-tight">
                        <span className="text-white">RIVALRY</span> <span className="text-volt">PRO+</span>
                    </h1>
                </div>
                <p className="text-xs text-zinc-400">
                    Sign in to manage your Rivalry Pro+ crews and subscriptions.
                </p>

                {CLERK_ENABLED && !showResetForm && (
                    <div className="space-y-2" data-testid="rivalry-pro-clerk-signin-cta">
                        <Link
                            to="/rivalry-pro/sign-in"
                            data-testid="rivalry-pro-clerk-signin-link"
                            className="brutal-btn-secondary text-sm py-2.5 w-full flex items-center justify-center gap-2"
                        >
                            <Sparkles className="w-4 h-4" />
                            Continue with Clerk
                        </Link>
                        {legacyLinkAllowed && !showLegacy && (
                            <button
                                type="button"
                                onClick={() => setShowLegacy(true)}
                                data-testid="rivalry-pro-legacy-toggle"
                                className="text-[10px] text-zinc-500 hover:text-zinc-300 w-full text-center underline underline-offset-2"
                            >
                                Use password instead (legacy)
                            </button>
                        )}
                    </div>
                )}

                {showLegacy && !showResetForm ? (
                    <form onSubmit={submit} className="space-y-3">
                        <label className="block">
                            <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Email</span>
                            <input
                                data-testid="rivalry-pro-login-email"
                                type="email"
                                autoFocus
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="brutal-input text-sm w-full mt-1"
                                placeholder="you@company.com"
                                autoComplete="email"
                            />
                        </label>
                        <label className="block">
                            <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Password</span>
                            <input
                                data-testid="rivalry-pro-login-password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="brutal-input text-sm w-full mt-1"
                                autoComplete="current-password"
                            />
                        </label>
                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="rivalry-pro-login-submit"
                            className="brutal-btn-primary text-sm py-2 w-full disabled:opacity-40"
                        >
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
                            Sign in
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowResetForm(true)}
                            data-testid="rivalry-pro-forgot-link"
                            className="text-[11px] text-cyber hover:underline w-full text-center"
                        >
                            Forgot password?
                        </button>
                    </form>
                ) : showResetForm ? (
                    <form onSubmit={submitReset} className="space-y-3" data-testid="rivalry-pro-reset-form">
                        <p className="text-xs text-zinc-300">
                            We'll email a magic link if the address matches a Rivalry Pro+ account.
                        </p>
                        <input
                            data-testid="rivalry-pro-reset-email"
                            type="email"
                            autoFocus
                            value={resetEmail}
                            onChange={(e) => setResetEmail(e.target.value)}
                            className="brutal-input text-sm w-full"
                            placeholder="you@company.com"
                        />
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setShowResetForm(false)}
                                className="brutal-btn-ghost text-xs py-2 flex-1"
                            >
                                Back
                            </button>
                            <button
                                type="submit"
                                disabled={busy}
                                data-testid="rivalry-pro-reset-submit"
                                className="brutal-btn-primary text-xs py-2 flex-1 disabled:opacity-40"
                            >
                                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Send link"}
                            </button>
                        </div>
                    </form>
                ) : null}

                <div className="pt-2 border-t border-[#222]">
                    <p className="text-[10px] text-zinc-500" data-testid="rivalry-pro-login-footer-copy">
                        {CLERK_ENABLED ? (
                            <>
                                Not a Rivalry Pro+ customer yet?{" "}
                                <Link
                                    to="/rivalry-pro/sign-up"
                                    data-testid="rivalry-pro-login-signup-link"
                                    className="text-volt hover:underline"
                                >
                                    Create an account and sign up!
                                </Link>
                            </>
                        ) : (
                            "Not a Rivalry Pro+ customer yet? Reach out to be added to the program."
                        )}
                    </p>
                </div>
            </div>
        </div>
    );
}
